import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash, Plus, ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';

const OpinionAdminList = () => {
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalOpinions: 0,
    pageSize: 10
  });
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch opinions when page or status filter changes
  useEffect(() => {
    fetchOpinions(pagination.currentPage, statusFilter);
  }, [pagination.currentPage, statusFilter]);

  // Function to fetch opinions from the API
  const fetchOpinions = async (page = 1, status = 'all') => {
    try {
      setLoading(true);
      
      // Build the API URL with filters
      let url = `/api/opinions/list?page=${page}`;
      if (status !== 'all') {
        url += `&status=${status}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      setOpinions(data.opinions || []);
      setPagination(data.pagination || {
        currentPage: page,
        totalPages: 1,
        totalOpinions: 0,
        pageSize: 10
      });
      
      setError(null);
    } catch (err) {
      setError(`Failed to load opinions: ${err.message}`);
      setOpinions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  // Handle opinion deletion
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this opinion? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/opinions/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      // Refresh the list after deletion
      fetchOpinions(pagination.currentPage, statusFilter);
    } catch (err) {
      alert(`Failed to delete opinion: ${err.message}`);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return dateString;
    }
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    const badgeStyles = {
      draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      published: 'bg-green-100 text-green-800 border-green-200',
      archived: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeStyles[status] || badgeStyles.draft}`;
  };

  if (loading && opinions.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with add button and filters */}
      <div className="flex flex-wrap justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Manage Opinions</h2>
        
        <div className="flex items-center space-x-4">
          {/* Status filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          
          {/* Add new opinion */}
          <Link
            to="/admin/opinions/add"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            New Opinion
          </Link>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md">
          {error}
          <button 
            onClick={() => fetchOpinions(1, statusFilter)} 
            className="ml-2 text-red-900 underline"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {opinions.length === 0 && !loading && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-700 mb-2">No opinions found</h3>
          <p className="text-gray-500 mb-4">
            {statusFilter === 'all' 
              ? 'Get started by creating your first opinion.'
              : `No opinions with "${statusFilter}" status.`}
          </p>
          <Link
            to="/admin/opinions/add"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            Create Opinion
          </Link>
        </div>
      )}
      
      {/* Opinions list */}
      {opinions.length > 0 && (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <ul className="divide-y divide-gray-200">
            {opinions.map(opinion => (
              <li key={opinion.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/admin/opinions/${opinion.id}/edit`}
                      className="text-lg font-medium text-indigo-600 hover:text-indigo-800 truncate"
                    >
                      {opinion.title}
                    </Link>
                    
                    <div className="mt-1 flex items-center">
                      <span className="text-sm text-gray-600 truncate">
                        By {opinion.author}
                      </span>
                      
                      <span className="mx-2 text-gray-300">•</span>
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(opinion.publication_date)}
                      </div>
                      
                      <span className="mx-2 text-gray-300">•</span>
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock size={14} className="mr-1" />
                        {opinion.reading_time_minutes} min read
                      </div>
                    </div>
                    
                    <div className="mt-2">
                      <span className={getStatusBadge(opinion.status)}>
                        {opinion.status.charAt(0).toUpperCase() + opinion.status.slice(1)}
                      </span>
                    </div>
                    
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                      {opinion.content_snippet}
                    </p>
                  </div>
                  
                  <div className="ml-4 flex-shrink-0 flex">
                    <Link
                      to={`/admin/opinions/${opinion.id}/edit`}
                      className="mr-2 text-gray-400 hover:text-indigo-600"
                      title="Edit"
                    >
                      <Edit size={20} />
                    </Link>
                    
                    <button
                      onClick={() => handleDelete(opinion.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className={`flex items-center space-x-1 px-3 py-1 rounded ${
              pagination.currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </button>
          
          <span className="text-sm text-gray-700">
            Page {pagination.currentPage} of {pagination.totalPages}
            {pagination.totalOpinions > 0 && ` (${pagination.totalOpinions} total)`}
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`flex items-center space-x-1 px-3 py-1 rounded ${
              pagination.currentPage === pagination.totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span>Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default OpinionAdminList;